import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

interface TocItem {
  id: string;
  label: string;
}

const tocItems: TocItem[] = [
  { id: 'intro', label: '1. Introducción' },
  { id: 'que-son', label: '2. ¿Qué son las Cookies?' },
  { id: 'tipos', label: '3. Tipos de Cookies' },
  { id: 'terceros', label: '4. Cookies de Terceros' },
  { id: 'finalidad', label: '5. Finalidad de las Cookies' },
  { id: 'gestion', label: '6. Gestión de Cookies' },
  { id: 'consentimiento', label: '7. Consentimiento' },
  { id: 'actualizaciones', label: '8. Actualizaciones' },
  { id: 'contacto', label: '9. Contacto' },
];

function SectionBlock({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="text-lg font-bold text-foreground-950 mb-4">{title}</h2>
      <div className="text-foreground-700 text-sm leading-relaxed space-y-3">
        {children}
      </div>
    </section>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="space-y-2 pl-5">
      {items.map((item, i) => (
        <li key={i} className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-[0.6em] before:w-1.5 before:h-1.5 before:rounded-full before:bg-accent-500/60 text-foreground-600">
          {item}
        </li>
      ))}
    </ul>
  );
}

function CookieTypeCard({ icon, title, ejemplos, duracion, descripcion }: { icon: string; title: string; ejemplos: string; duracion: string; descripcion: string }) {
  return (
    <div className="bg-background-50 border border-background-200/70 rounded-xl p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-accent-100 flex items-center justify-center shrink-0">
          <i className={`${icon} text-accent-600 text-lg`}></i>
        </div>
        <div>
          <h4 className="font-semibold text-foreground-900 text-sm">{title}</h4>
          <p className="text-foreground-500 text-xs">{descripcion}</p>
        </div>
      </div>
      <div className="space-y-2 text-xs">
        <div>
          <span className="font-semibold text-foreground-800">Ejemplos:</span>{' '}
          <span className="text-foreground-600">{ejemplos}</span>
        </div>
        <div>
          <span className="font-semibold text-foreground-800">Duración:</span>{' '}
          <span className="text-foreground-600">{duracion}</span>
        </div>
      </div>
    </div>
  );
}

export default function CookiesPage() {
  const [activeSection, setActiveSection] = useState('intro');

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Header />

      {/* Hero */}
      <section className="relative pt-20 pb-12 md:pt-28 md:pb-16 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://readdy.ai/api/search-image?query=abstract%20minimalist%20baking%20cookies%20themed%20concept%20with%20soft%20warm%20beige%20caramel%20and%20cream%20tones%2C%20subtle%20organic%20circular%20patterns%20like%20cookie%20shapes%2C%20gentle%20rounded%20geometric%20forms%2C%20warm%20inviting%20atmosphere%2C%20sophisticated%20elegant%20design%2C%20light%20amber%20and%20toasted%20brown%20accents%2C%20clean%20modern%20aesthetic%2C%20no%20text%20no%20logos&width=1600&height=600&seq=cookies-hero-bg&orientation=landscape)`,
          }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/95 to-background-50"></div>
        <div className="relative max-w-5xl mx-auto px-4 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-5">
            <i className="ri-file-settings-line"></i>
            Transparencia y Control
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-3">Política de Cookies</h1>
          <p className="text-foreground-500 text-sm max-w-2xl mx-auto">
            Última actualización: 9 de junio de 2026. Esta política explica qué son las cookies, cómo las utilizamos en RadioFrecuenciaWeb y cómo podés gestionarlas.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="flex-1 pb-20">
        <div className="max-w-5xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10">
            {/* Sidebar TOC */}
            <aside className="lg:w-60 shrink-0">
              <div className="sticky top-24 bg-background-100/80 rounded-2xl border border-background-200/70 p-4">
                <p className="text-[10px] font-bold text-foreground-400 uppercase tracking-widest mb-3">Contenido</p>
                <nav className="space-y-0.5">
                  {tocItems.map((item) => (
                    <a
                      key={item.id}
                      href={`#${item.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        setActiveSection(item.id);
                        document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      className={`block px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer ${
                        activeSection === item.id
                          ? 'bg-accent-500 text-background-50'
                          : 'text-foreground-500 hover:text-foreground-800 hover:bg-background-200/60'
                      }`}
                    >
                      {item.label}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <div className="flex-1 min-w-0 space-y-10">
              <SectionBlock id="intro" title="1. Introducción">
                <p>
                  En RadioFrecuenciaWeb utilizamos cookies y tecnologías similares para mejorar tu experiencia
                  de navegación, analizar el tráfico y personalizar el contenido. Esta Política de Cookies
                  explica de forma clara y transparente qué son, qué tipos utilizamos y cómo podés controlarlas.
                </p>
                <p>
                  Esta política complementa nuestra{' '}
                  <Link to="/privacy" className="text-accent-600 hover:text-accent-700 underline cursor-pointer font-medium">
                    Política de Privacidad
                  </Link>
                  , donde también abordamos el uso de cookies en el contexto general de protección de datos.
                </p>
                <p>
                  Al navegar por RadioFrecuenciaWeb, aceptás el uso de cookies de acuerdo con lo establecido
                  en esta política. Si no estás de acuerdo, podés ajustar la configuración de tu navegador
                  o no utilizar la Plataforma.
                </p>
              </SectionBlock>

              <SectionBlock id="que-son" title="2. ¿Qué son las Cookies?">
                <p>
                  Una cookie es un pequeño archivo de texto que un sitio web almacena en tu dispositivo
                  (computadora, tablet o teléfono) cuando lo visitás. Las cookies permiten que el sitio
                  web recuerde información sobre tu visita, como tu idioma preferido, tu sesión iniciada
                  y otras configuraciones.
                </p>
                <p>
                  Esto hace que tu navegación sea más fluida, rápida y personalizada. Las cookies no contienen
                  información personal identificable por sí mismas, pero pueden vincularse a datos almacenados
                  en nuestros sistemas cuando iniciás sesión.
                </p>
                <p>
                  Además de cookies tradicionales, utilizamos tecnologías similares como:
                </p>
                <BulletList items={[
                  '<strong>Local Storage:</strong> Permite almacenar datos en tu navegador de forma persistente, como preferencias de visualización.',
                  '<strong>Session Storage:</strong> Similar al Local Storage, pero los datos se eliminan al cerrar la pestaña del navegador.',
                  '<strong>Web Beacons:</strong> Pequeñas imágenes transparentes que nos permiten saber si abriste un correo electrónico o visitaste ciertas páginas.',
                ]} />
              </SectionBlock>

              <SectionBlock id="tipos" title="3. Tipos de Cookies que Utilizamos">
                <p>
                  En RadioFrecuenciaWeb clasificamos las cookies según su función y duración. A continuación
                  detallamos cada tipo:
                </p>

                <div className="grid grid-cols-1 gap-3 mt-2 not-prose">
                  <CookieTypeCard
                    icon="ri-lock-line"
                    title="Cookies Esenciales o Técnicas"
                    descripcion="Necesarias para el funcionamiento básico de la Plataforma"
                    ejemplos="Cookie de sesión de usuario, token de autenticación, preferencia de consentimiento de cookies"
                    duracion="Sesión (se eliminan al cerrar el navegador) o persistentes hasta 30 días"
                  />
                  <CookieTypeCard
                    icon="ri-bar-chart-line"
                    title="Cookies de Rendimiento y Análisis"
                    descripcion="Recopilan información sobre cómo los usuarios interactúan con la Plataforma"
                    ejemplos="Google Analytics (_ga, _gid), páginas más visitadas, tiempo de permanencia, eventos de clic"
                    duracion="Persistentes, entre 1 día y 2 años"
                  />
                  <CookieTypeCard
                    icon="ri-settings-3-line"
                    title="Cookies de Funcionalidad"
                    descripcion="Permiten recordar preferencias para personalizar tu experiencia"
                    ejemplos="Idioma preferido, región, género musical favorito, modo de visualización, volumen del reproductor"
                    duracion="Persistentes, entre 30 días y 1 año"
                  />
                  <CookieTypeCard
                    icon="ri-advertisement-line"
                    title="Cookies de Publicidad"
                    descripcion="Utilizadas para mostrar anuncios relevantes dentro de la Plataforma"
                    ejemplos="Identificador de anunciante, frecuencia de visualización de banners, interacción con anuncios"
                    duracion="Persistentes, hasta 90 días"
                  />
                </div>
              </SectionBlock>

              <SectionBlock id="terceros" title="4. Cookies de Terceros">
                <p>
                  Algunas cookies utilizadas en RadioFrecuenciaWeb son establecidas por servicios de terceros
                  que integran funcionalidades en nuestra Plataforma. Estas cookies están sujetas a las
                  políticas de privacidad de dichos terceros. Los principales servicios son:
                </p>
                <BulletList items={[
                  '<strong>Google Analytics:</strong> Para análisis de tráfico y comportamiento de usuarios. Podés inhabilitarlo desde <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer" class="text-accent-600 hover:text-accent-700 underline cursor-pointer">el complemento de inhabilitación de Google Analytics</a>.',
                  '<strong>Google Fonts:</strong> Para cargar tipografías web. Google puede recopilar datos sobre la descarga de fuentes.',
                  '<strong>Redes Sociales:</strong> Al integrar botones de compartir (Facebook, Twitter/X, Instagram), estas plataformas pueden establecer sus propias cookies.',
                  '<strong>Reproductor de Audio:</strong> Para la funcionalidad de streaming y reproducción de audio en tiempo real.',
                ]} />
                <p>
                  No tenemos control directo sobre las cookies de terceros. Te recomendamos revisar las
                  políticas de privacidad de cada proveedor para obtener información detallada sobre sus prácticas.
                </p>
              </SectionBlock>

              <SectionBlock id="finalidad" title="5. Finalidad de las Cookies">
                <p>Las cookies que utilizamos en RadioFrecuenciaWeb tienen las siguientes finalidades:</p>
                <BulletList items={[
                  '<strong>Autenticación:</strong> Mantener tu sesión iniciada mientras navegás por la Plataforma sin necesidad de ingresar tus credenciales repetidamente.',
                  '<strong>Seguridad:</strong> Detectar actividades sospechosas, prevenir fraudes y proteger tus datos y los de la Plataforma.',
                  '<strong>Personalización:</strong> Recordar tus preferencias para ofrecerte contenido relevante, como radios sugeridas según tus gustos musicales.',
                  '<strong>Análisis:</strong> Entender cómo se utiliza la Plataforma para identificar áreas de mejora y optimizar el rendimiento.',
                  '<strong>Publicidad:</strong> Mostrar anuncios relevantes dentro de la Plataforma, evitando mostrar repetidamente los mismos anuncios.',
                  '<strong>Funcionalidad del Reproductor:</strong> Mantener la reproducción de audio continua mientras navegás entre páginas de la Plataforma.',
                ]} />
              </SectionBlock>

              <SectionBlock id="gestion" title="6. Gestión y Desactivación de Cookies">
                <p>
                  Tenés control total sobre las cookies. Podés gestionarlas, bloquearlas o eliminarlas en
                  cualquier momento desde la configuración de tu navegador:
                </p>
                <BulletList items={[
                  '<strong>Google Chrome:</strong> Configuración → Privacidad y seguridad → Cookies y otros datos de sitios.',
                  '<strong>Mozilla Firefox:</strong> Opciones → Privacidad y Seguridad → Cookies y datos del sitio.',
                  '<strong>Safari:</strong> Preferencias → Privacidad → Cookies y datos de sitios web.',
                  '<strong>Microsoft Edge:</strong> Configuración → Privacidad, búsqueda y servicios → Cookies.',
                  '<strong>Opera:</strong> Configuración → Privacidad y seguridad → Cookies.',
                ]} />
                <p>
                  También podés optar por navegar en modo incógnito o privado, que elimina automáticamente
                  las cookies al cerrar la ventana.
                </p>

                <div className="mt-4 p-4 bg-accent-50 border border-accent-200/60 rounded-xl">
                  <p className="text-sm text-foreground-700 flex items-start gap-2">
                    <i className="ri-information-line text-accent-500 mt-0.5 shrink-0"></i>
                    <span>
                      <strong>Importante:</strong> Deshabilitar las cookies esenciales puede afectar el funcionamiento
                      de la Plataforma. Por ejemplo, no podrás mantener tu sesión iniciada, guardar tus preferencias
                      ni utilizar ciertas funciones como el chat en vivo o el envío de mensajes a las radios.
                    </span>
                  </p>
                </div>
              </SectionBlock>

              <SectionBlock id="consentimiento" title="7. Consentimiento">
                <p>
                  La primera vez que visitás RadioFrecuenciaWeb, te mostramos un banner de cookies donde podés
                  aceptar todas las cookies, rechazar las no esenciales o personalizar tus preferencias.
                </p>
                <p>
                  Tu consentimiento se aplica a los siguientes dominios: radiofrecuenciaweb.com y sus subdominios.
                  Podés cambiar tus preferencias en cualquier momento desde la sección de configuración de
                  cookies disponible en el pie de página de la Plataforma.
                </p>
                <p>
                  Conservamos el registro de tu consentimiento durante un período máximo de 2 años, tras
                  el cual te solicitaremos nuevamente que confirmes tus preferencias.
                </p>
              </SectionBlock>

              <SectionBlock id="actualizaciones" title="8. Actualizaciones de esta Política">
                <p>
                  Podemos actualizar esta Política de Cookies periódicamente para reflejar cambios en nuestras
                  prácticas, en las tecnologías que utilizamos o por requisitos legales. La fecha de "Última
                  actualización" al inicio de esta página indica cuándo se realizaron los cambios más recientes.
                </p>
                <p>
                  Te recomendamos revisar esta política cada cierto tiempo para estar al tanto de cómo
                  utilizamos las cookies. Los cambios sustanciales serán notificados mediante un aviso
                  destacado en la Plataforma.
                </p>
              </SectionBlock>

              <SectionBlock id="contacto" title="9. Contacto">
                <p>
                  Si tenés preguntas sobre esta Política de Cookies o sobre cómo tratamos tus datos,
                  no dudes en contactarnos:
                </p>
                <BulletList items={[
                  'Correo electrónico: privacy@radiofrecuenciaweb.com',
                  'Formulario de contacto disponible en la Plataforma.',
                  'Dirección postal: Santa Rosa, California, Estados Unidos.',
                  'Oficial de Protección de Datos: dpo@radiofrecuenciaweb.com',
                ]} />
              </SectionBlock>

              {/* Bottom CTA */}
              <div className="mt-10 p-6 bg-background-100/80 rounded-2xl border border-background-200/70 text-center">
                <p className="text-foreground-700 text-sm mb-3">¿Necesitás más información sobre nuestras políticas?</p>
                <div className="flex items-center justify-center gap-3 flex-wrap">
                  <Link to="/privacy" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-shield-check-line mr-1.5"></i>
                    Política de Privacidad
                  </Link>
                  <Link to="/terms" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-file-text-line mr-1.5"></i>
                    Términos y Condiciones
                  </Link>
                  <Link to="/radio-rules" className="px-5 py-2.5 rounded-xl bg-background-50 border border-background-200 text-sm font-medium text-foreground-700 hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-radio-line mr-1.5"></i>
                    Reglas para Radios
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}